import { CrossFilterDimension } from "hooks/useCrossFilter"
import { COOP_EMPLOYEE_TYPE, HrReportRow, NOT_SET, yesNo } from "models/hr-reports"

// Dimensions shared by the HR reports. Keys are used by the charts, dropdowns and tables.
export const HR_DIM = {
    employeeType: "employeeType",
    department: "department",
    division: "division",
    manager: "manager",
    positionType: "positionType",
    designation: "designation",
    officeLocation: "officeLocation",
    gender: "gender",
    indigenous: "indigenous",
    esa: "esa",
    identity: "identity",
} as const

export const NO_DESIGNATION = "No designation"

const one = (value: string | null | undefined) => [value?.trim() || NOT_SET]

export const hrReportDimensions = <T extends HrReportRow>(): CrossFilterDimension<T>[] => [
    { key: HR_DIM.employeeType, label: "Employee Type", values: r => one(r.employeeType) },
    { key: HR_DIM.department, label: "Department", values: r => one(r.department) },
    { key: HR_DIM.division, label: "Division", values: r => one(r.division) },
    { key: HR_DIM.manager, label: "Manager", values: r => one(r.manager) },
    { key: HR_DIM.positionType, label: "Position Type", values: r => one(r.positionType) },
    { key: HR_DIM.designation, label: "Designation", values: r => (r.designations.length ? r.designations : [NO_DESIGNATION]) },
    { key: HR_DIM.officeLocation, label: "Office Location", values: r => one(r.officeLocation) },
    { key: HR_DIM.gender, label: "Gender", values: r => one(r.gender) },
    { key: HR_DIM.indigenous, label: "Indigenous", values: r => [yesNo(r.indigenous)] },
    { key: HR_DIM.esa, label: "ESA Exempt", values: r => [yesNo(r.esaExempt)] },
    { key: HR_DIM.identity, label: "Identity", values: r => one(r.identity) },
]

// Dropdowns at the top of Demographics and Retention
export const HR_TOP_FILTERS = [
    { key: HR_DIM.employeeType, label: "Employee Type" },
    { key: HR_DIM.department, label: "Department" },
    { key: HR_DIM.division, label: "Division" },
    { key: HR_DIM.manager, label: "Manager" },
]

export const isCoop = (r: HrReportRow) => r.employeeType === COOP_EMPLOYEE_TYPE

// Rows grouped by department, largest first
export const groupByDepartment = <T extends HrReportRow>(rows: T[]) => {
    const groups = new Map<string, T[]>()
    rows.forEach(r => {
        const key = r.department?.trim() || NOT_SET
        groups.set(key, [...(groups.get(key) ?? []), r])
    })
    return Array.from(groups, ([department, people]) => ({ department, people }))
        .sort((a, b) => b.people.length - a.people.length || a.department.localeCompare(b.department))
}

// Dates come from the API as ISO strings at midnight UTC; read the UTC parts so the day never shifts.
export const formatDate = (iso: string | null) => {
    if (!iso) return "-"
    const d = new Date(iso)
    return d.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" })
}

export const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]

// "1 year", "2 years"... used for years of service
export const yearsLabel = (years: number) => `${years} ${years === 1 ? "year" : "years"}`
